import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';

import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import {
  approveRecord,
  deleteRecord,
  fetchRecord,
  reopenRecord,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecord, ProblemDetail } from '../../../types/syntiro_honey';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { aliasOutcomeFeedback } from './alias_feedback';
import { readBoundingBox } from './bounding_box';
import MappingStatusChip from './MappingStatusChip';
import MetricStory from './MetricStory';
import RecordDetailSummary from './RecordDetailSummary';
import RecordEditForm from './RecordEditForm';
import RecordHistoryList from './RecordHistoryList';
import RecordMarkers from './RecordMarkers';
import RecordStatusChip from './RecordStatusChip';
import { resolveRecordEntity } from './record_entity';
import SourceFileLink from './SourceFileLink';
import SourceDocumentTab from './source_doc/SourceDocumentTab';
import { resolveSourceFormat, sourceTabLabel } from './source_doc/source_format';

interface RecordDetailModalProps {
  /** Viewer mode: the record is inspectable but not editable/approvable. */
  readOnly?: boolean;
  /** The record to show, or null when the modal is closed. */
  recordId: string | null;
  /** Close handler. */
  onClose: () => void;
  /** Called after any successful mutation so the parent can refresh the list. */
  onRecordMutated: () => void;
  /** Record ids on the current page, in display order, for Prev/Next stepping. */
  recordIds: Array<string>;
  /** Navigate to another record without closing the modal. */
  onNavigate: (recordId: string) => void;
}

/** Tabs of the detail modal: the editable review, the source document, the metric story, and provenance. */
type DetailTab = 'review' | 'source' | 'story' | 'history';

/**
 * Detail modal for a single staged record. A large dialog whose primary "Review"
 * tab shows the source spreadsheet preview and the editable metric/data fields
 * side-by-side, with the metric's story-over-time and the record's audit trail as
 * secondary tabs. Prev/Next step through the current page. Approval is
 * guarded client-side when no metric is mapped (the server enforces the same 400).
 * @returns {JSX.Element}
 */
export default function RecordDetailModal({
  recordId,
  onClose,
  onRecordMutated,
  recordIds,
  onNavigate,
  readOnly = false
}: RecordDetailModalProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [record, setRecord] = useState<EsgRecord | null>(null);
  const [loadProblem, setLoadProblem] = useState<ProblemDetail | null>(null);
  const [actionProblem, setActionProblem] = useState<ProblemDetail | null>(null);
  const [actionBusy, setActionBusy] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [tab, setTab] = useState<DetailTab>('review');

  // Mutations are kicked off from a click-time closure over `recordId`, so by the
  // time one resolves, comparing against that closure's value can't detect that
  // Prev/Next has since moved on — this ref always holds the current prop.
  const recordIdRef = useRef(recordId);
  recordIdRef.current = recordId;

  // One memoised source of truth for the anchored bbox: a fresh object per render
  // would re-trigger the PDF view's effect (re-download + concurrent render on the
  // same canvas) on every unrelated state change, e.g. an Approve click.
  const bbox = useMemo(
    () => (record === null ? null : readBoundingBox(record.rawPayload)),
    [record]
  );

  // Which viewer the source tab offers, and therefore what to call it.
  const sourceFormat =
    record === null ? null : resolveSourceFormat(record.fileName, record.fileMimeType);

  useEffect(() => {
    setRecord(null);
    setLoadProblem(null);
    setActionProblem(null);
    setTab('review');
    if (recordId === null) {
      return;
    }
    let cancelled = false;
    fetchRecord(recordId)
      .then((fetched) => {
        if (!cancelled) {
          setRecord(fetched);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadProblem(toProblemDetail(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  const handleMutated = (updated: EsgRecord): void => {
    // Only apply the response to the displayed record if it's still the one on
    // screen — Prev/Next during an in-flight approve/reopen/edit must not let a
    // stale response overwrite whatever record the reviewer has since moved to.
    if (updated.id === recordIdRef.current) {
      setRecord(updated);
    }
    onRecordMutated();
  };

  const handleApprove = async (): Promise<void> => {
    if (record === null) {
      return;
    }
    setActionBusy(true);
    try {
      const approved = await approveRecord(record.id);
      handleMutated(approved);
      setActionProblem(null);
      // Approving a suggested mapping also confirms it (ADR-0002); the response
      // says what that taught the learned rules — surface it exactly like the
      // confirm button does. Null/absent (nothing confirmed, could-not-learn,
      // or an older backend) stays quiet: no claim without the outcome to back it.
      if (approved.aliasOutcome !== undefined && approved.aliasOutcome !== null) {
        setFeedbackData(aliasOutcomeFeedback(approved));
      }
    } catch (error: unknown) {
      setActionProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
    }
  };

  const handleReopen = async (): Promise<void> => {
    if (record === null) {
      return;
    }
    setActionBusy(true);
    try {
      // Moves the record back to pending review so it can be corrected/re-approved
      // or deleted — the escape hatch for a mistaken/duplicate approval (Maxine #12).
      handleMutated(await reopenRecord(record.id));
      setActionProblem(null);
    } catch (error: unknown) {
      setActionProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (record === null) {
      return;
    }
    setActionBusy(true);
    try {
      await deleteRecord(record.id);
      setActionProblem(null);
      onRecordMutated();
      onClose();
    } catch (error: unknown) {
      setActionProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
    }
  };

  const approveBlockedReason =
    record === null
      ? null
      : record.metricId === null
        ? 'Map a metric first'
        : record.status === 'APPROVED'
          ? 'Already approved'
          : null;
  const isApproved = record?.status === 'APPROVED';

  const currentIndex = recordId === null ? -1 : recordIds.indexOf(recordId);
  const prevId = currentIndex > 0 ? recordIds[currentIndex - 1] : null;
  const nextId =
    currentIndex >= 0 && currentIndex < recordIds.length - 1 ? recordIds[currentIndex + 1] : null;

  return (
    <Dialog
      open={recordId !== null}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 3, pt: 2.5, pb: 1 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Subheading weight="Bold" sx={{ wordBreak: 'break-word' }}>
            {record?.rawLabel ?? 'Record detail'}
          </Subheading>
          {record !== null && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <RecordStatusChip status={record.status} />
                <MappingStatusChip
                  status={record.mappingStatus}
                  approved={record.status === 'APPROVED'}
                />
              </Stack>
              <CaptionText>
                {resolveRecordEntity(record)?.name ?? '—'} · {record.periodLabel ?? 'No period'}
              </CaptionText>
            </>
          )}
        </Stack>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>

      {record !== null && (
        <Tabs
          value={tab}
          onChange={(_, value: DetailTab) => setTab(value)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="review" label="Review" />
          <Tab value="source" label={sourceTabLabel(sourceFormat ?? 'UNSUPPORTED')} />
          <Tab value="story" label="Story over time" />
          <Tab value="history" label="History" />
        </Tabs>
      )}

      <DialogContent dividers>
        {loadProblem !== null && (
          <Alert severity="error">
            {loadProblem.detail ?? loadProblem.title ?? 'Could not load the record.'}
          </Alert>
        )}
        {record === null && loadProblem === null && (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        )}
        {record !== null && (
          <Stack spacing={2}>
            {actionProblem !== null && (
              <Alert severity="error" onClose={() => setActionProblem(null)}>
                {actionProblem.detail ?? actionProblem.title ?? 'Action failed.'}
              </Alert>
            )}

            {tab === 'review' && (
              <Stack spacing={3}>
                {sourceFormat !== null && sourceFormat !== 'UNSUPPORTED' && (
                  <PrimaryButton
                    startIcon={<CenterFocusStrongOutlinedIcon />}
                    onClick={() => setTab('source')}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Inspect value
                  </PrimaryButton>
                )}
                <RecordDetailSummary record={record} />
                {!readOnly && (
                  <>
                    <Divider />
                    <RecordMarkers record={record} onChanged={handleMutated} />
                  </>
                )}
                <Divider />
                {!readOnly &&
                  (isApproved ? (
                    // An approved record is live and immutable (the server rejects edits
                    // with a 409). Rather than a dead end, offer to reopen it for
                    // correction — moving it back to pending review (Maxine #12).
                    <Alert
                      severity="success"
                      action={
                        <PrimaryButton
                          palette_color="secondary"
                          disabled={actionBusy}
                          loading={actionBusy}
                          onClick={() => void handleReopen()}
                        >
                          Reopen for correction
                        </PrimaryButton>
                      }
                    >
                      This record is approved and live. Reopen it to edit, re-approve or delete it.
                    </Alert>
                  ) : (
                    <RecordEditForm key={record.id} record={record} onSaved={handleMutated} />
                  ))}
              </Stack>
            )}

            {/* Mounted only when the tab is shown, so opening it fetches the
                original document and lands on the cited cell/page/slide — which is
                also what the Review tab's "Inspect value" button triggers. */}
            {tab === 'source' && <SourceDocumentTab record={record} bbox={bbox} />}

            {tab === 'story' &&
              (record.metricId !== null ? (
                <MetricStory metricId={record.metricId} onApproved={onRecordMutated} />
              ) : (
                <Alert severity="info">
                  Map a metric to this record to see its story over time.
                </Alert>
              ))}

            {tab === 'history' && (
              <Stack spacing={2}>
                <SourceFileLink record={record} />
                <Divider />
                <CaptionText weight="Medium">Audit trail</CaptionText>
                {/* Mounted only when the tab is shown, so opening it fetches the
                    trail — the append-only log is the source of truth for how
                    this record reached its current state. */}
                <RecordHistoryList recordId={record.id} />
                {/* The unabridged extraction output (provenance): the summary and
                    source tabs read selected keys only, so a dispute over what the
                    model ACTUALLY returned needs the raw payload viewable here. */}
                <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                    <CaptionText weight="Medium">
                      Original extraction payload (provenance)
                    </CaptionText>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: 480,
                        bgcolor: 'action.hover',
                        p: 1.5,
                        borderRadius: 1
                      }}
                    >
                      {JSON.stringify(record.rawPayload, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <GhostButton
            dark
            outlined
            disabled={prevId === null}
            onClick={() => prevId !== null && onNavigate(prevId)}
          >
            Previous
          </GhostButton>
          <GhostButton
            dark
            outlined
            disabled={nextId === null}
            onClick={() => nextId !== null && onNavigate(nextId)}
          >
            Next
          </GhostButton>
        </Stack>
        {!readOnly && record !== null && !isApproved && (
          <>
            <GhostButton dark outlined disabled={actionBusy} onClick={() => setConfirmDelete(true)}>
              Delete
            </GhostButton>
            <Tooltip title={approveBlockedReason ?? ''}>
              <span>
                <PrimaryButton
                  palette_color="success"
                  disabled={actionBusy || approveBlockedReason !== null}
                  loading={actionBusy}
                  onClick={() => void handleApprove()}
                >
                  Approve
                </PrimaryButton>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>

      <ConfirmationModal
        open={confirmDelete}
        title="Delete record?"
        description="Soft-delete this record. Approved records cannot be deleted."
        confirm_button_text="Delete"
        confirm_loading={actionBusy}
        handleCloseModal={() => setConfirmDelete(false)}
        // Returning the promise lets the modal await completion before closing.
        handleConfirm={handleDelete}
      />
    </Dialog>
  );
}
